import { Sparkles } from "lucide-react"
import { useCallback } from "react"
import { Button } from "@/components/ui/primitives"
import { WorkspaceDialog } from "@/components/workspace/workspace-dialog"
import { useTranslation } from "@/hooks/use-translation"
import { confirm } from "@/lib/confirm"
import {
  lowestResolutionOption,
  resolutionParamOptions,
  shouldConfirmFirstGenResolution,
} from "@/lib/shot-video-settings"
import type { GenerationModelCatalogEntry } from "@/types/workspace"

type GateArgs = {
  /** Кадр без опорного изображения: перед генерацией спрашиваем подтверждение. */
  needsReferenceFrame: boolean
  hasVideo: boolean
  effectiveResolution: string
  model: GenerationModelCatalogEntry | null | undefined
  /** Открыть диалог выбора разрешения (состояние живёт у экрана: он сбрасывает его при смене шота). */
  setDialogOpen: (open: boolean) => void
  /** После кадра и до диалога разрешения: превиз / аниматик. false — не генерировать. */
  beforeResolution?: () => Promise<boolean>
}

/** Проверки перед генерацией видео шота: нет опорного кадра, превиз/аниматик, дорогое разрешение. */
export function useFirstGenResolutionGate({
  needsReferenceFrame,
  hasVideo,
  effectiveResolution,
  model,
  setDialogOpen,
  beforeResolution,
}: GateArgs) {
  const { t } = useTranslation(["shot", "common"])

  const beforeArm = useCallback(async () => {
    if (needsReferenceFrame) {
      const ok = await confirm({
        title: t("videoMissingFrameConfirm.title"),
        message: t("videoMissingFrameConfirm.message"),
        confirmLabel: t("videoMissingFrameConfirm.confirm"),
      })
      if (!ok) return false
    }
    if (beforeResolution && !(await beforeResolution())) return false
    if (shouldConfirmFirstGenResolution({ hasVideo, effectiveResolution, model })) {
      setDialogOpen(true)
      return false
    }
    return true
  }, [beforeResolution, effectiveResolution, hasVideo, model, needsReferenceFrame, setDialogOpen, t])

  const minOption = lowestResolutionOption(resolutionParamOptions(model))

  return { beforeArm, minOption }
}

type DialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  effectiveResolution: string
  minOption: string | null
  busy: boolean
  /** null — генерировать в выбранном разрешении. */
  onGenerate: (videoResolutionOverride: string | null) => void
}

export function FirstGenResolutionDialog({
  open,
  onOpenChange,
  effectiveResolution,
  minOption,
  busy,
  onGenerate,
}: DialogProps) {
  const { t } = useTranslation(["shot", "common"])
  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("firstGenResolutionConfirm.title")}
      closeAriaLabel={t("close", { ns: "common" })}
      size="md"
      nested
      bodyClassName="p-5 pt-4"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              onOpenChange(false)
              onGenerate(null)
            }}
            disabled={busy || !effectiveResolution}
          >
            <Sparkles className="h-3 w-3" />
            {t("firstGenResolutionConfirm.generateAt", { resolution: effectiveResolution || "—" })}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => {
              onOpenChange(false)
              if (!minOption) return
              onGenerate(minOption)
            }}
            disabled={!minOption || busy}
          >
            <Sparkles className="h-3 w-3" />
            {t("firstGenResolutionConfirm.generateMin", { resolution: minOption || "—" })}
          </Button>
        </div>
      }
    >
      <p className="text-sm text-ui-text-secondary">
        {t("firstGenResolutionConfirm.message", { resolution: minOption || "—" })}
      </p>
    </WorkspaceDialog>
  )
}
